import { DateLocalizationHelper } from "../../common/DateLocalizationHelper";
import { SearchSortButton } from "../../components/SearchSortButton";
import { translate } from "../../i18n/translate";
import { GlobalSearchService } from "../../services/GlobalSearchService";

type SearchItem = {
  url: string;
  title: string;
  abstract?: string;
  description?: string;
  publishedDate: string | Date;
  taxonomies?: string[];
  isRead?: boolean;
  externalUrl?: string;
  court?: string;
  caseNumber?: string | null;
};

export class ArticleListingCard {
  private readonly element: HTMLElement | null;
  private readonly searchService: GlobalSearchService | null;
  private readonly dateHelper: DateLocalizationHelper;
  private searchSortButton: SearchSortButton | null = null;
  private renderCallback: () => void;

  // intersection observer infrastructure
  private io: IntersectionObserver | null = null;
  private sentinel: HTMLDivElement | null = null;

  constructor(element: HTMLElement, searchService: GlobalSearchService) {
    this.dateHelper = DateLocalizationHelper.getInstance();
    this.renderCallback = this.render.bind(this);

    if (!element) {
      console.warn(
        "ArticleListingCard: No valid element provided, skipping initialization"
      );
      this.element = null;
      this.searchService = null;
      return;
    }

    if (!searchService) {
      console.warn(
        "ArticleListingCard: No valid search service provided, skipping initialization"
      );
      this.element = null;
      this.searchService = null;
      return;
    }

    this.element = element;
    this.searchService = searchService;

    this.searchSortButton = new SearchSortButton();

    this.searchService.addObserver(this.renderCallback);
    this.render();

    // setup infinite scroll after initial render
    this.setupInfiniteScroll();
  }

  private setupInfiniteScroll(): void {
    if (!this.element || !this.searchService) return;

    if (!this.sentinel) {
      this.sentinel = document.createElement("div");
      this.sentinel.className = "infinite-scroll-sentinel";
      this.sentinel.style.height = "1px";
      this.sentinel.style.width = "100%";
      this.sentinel.style.margin = "0";
      this.element.appendChild(this.sentinel);
    }

    if (!this.io) {
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.searchService?.loadNextPage();
            }
          });
        },
        {
          root: null,
          rootMargin: "300px",
          threshold: 0,
        }
      );
    }

    if (this.sentinel) {
      this.io.observe(this.sentinel);
    }
  }

  // Encode String to HTML encoded String using a TextNode (recognized by Veracode).
  // Copied pattern per documentation.
  private vcEncodeHtml(value: string | undefined | null): string {
    const text = document.createTextNode(value ?? "");
    const p = document.createElement("p");
    p.appendChild(text);
    return p.innerHTML;
  }

  // Only allow relative or same-origin URLs for internal navigation.
  private resolveInternalUrl(raw: string | undefined | null): string {
    const value = (raw || "").trim();
    if (!value) return "#";
    if (/[<>\r\n]/.test(value)) return "#";

    // Strictly allow relative paths (preferred)
    if (value.startsWith("/")) {
      return value;
    }

    // Allow absolute only if same-origin and http(s)
    try {
      const u = new URL(value, window.location.origin);
      if (
        (u.protocol === "http:" || u.protocol === "https:") &&
        u.origin === window.location.origin
      ) {
        return u.toString();
      }
    } catch {
      // ignore
    }
    return "#";
  }

  // Only allow http/https for external links; consider allowlisting hosts if needed.
  private resolveExternalUrl(raw: string | undefined | null): string {
    const value = (raw || "").trim();
    if (!value) return "#";
    if (/[<>\r\n]/.test(value)) return "#";

    try {
      const u = new URL(value, window.location.origin);
      if (u.protocol === "http:" || u.protocol === "https:") {
        return u.toString();
      }
    } catch {
      // ignore
    }
    return "#";
  }

  private buildActionIcon(): HTMLElement {
    // Safe, simple icon (no external sprite references)
    const span = document.createElement("span");
    span.className = "icon";
    span.setAttribute("aria-hidden", "true");
    span.textContent = "→";
    return span;
  }

  private buildCard(article: SearchItem): HTMLElement {
    const card = document.createElement("div");
    card.className = "card box block-link masonry-item";

    const boxContent = document.createElement("div");
    boxContent.className = "box__content";
    card.appendChild(boxContent);

    // Heading and link
    const h3 = document.createElement("h3");
    h3.className = "h3 card__heading";
    const titleLink = document.createElement("a");

    // Validate, then encode URL for href
    const safeTitleUrl = this.resolveInternalUrl(article.url);
    const encodedTitleUrl = encodeURI(safeTitleUrl);
    titleLink.setAttribute("href", encodedTitleUrl);

    // Encode text via vcEncodeHtml and assign via innerHTML
    titleLink.innerHTML = this.vcEncodeHtml(article.title || "");
    h3.appendChild(titleLink);
    boxContent.appendChild(h3);

    // Meta section (date, court, caseNumber)
    const metaWrap = document.createElement("div");
    metaWrap.style.display = "block";
    {
      const dateDiv = document.createElement("div");
      const formatted = this.dateHelper.formatPublishedDate(
        article.publishedDate,
        "DD.MM.YYYY",
        { prefix: translate("Insight.PublicationDate") }
      );
      dateDiv.innerHTML = this.vcEncodeHtml(formatted || "");
      metaWrap.appendChild(dateDiv);

      if (article.court && article.court.trim() !== "") {
        const courtDiv = document.createElement("div");
        courtDiv.style.display = "flex";

        const courtText = document.createElement("div");
        courtText.innerHTML = this.vcEncodeHtml(article.court || "");
        courtDiv.appendChild(courtText);

        const caseNo = article.caseNumber ?? "";
        if (caseNo && caseNo.trim() !== "") {
          const caseDiv = document.createElement("div");
          caseDiv.style.display = "list-item";
          caseDiv.style.marginLeft = "25px";
          caseDiv.innerHTML = this.vcEncodeHtml(caseNo);
          courtDiv.appendChild(caseDiv);
        }

        metaWrap.appendChild(courtDiv);
      }
    }
    boxContent.appendChild(metaWrap);

    // Taxonomies
    const tagsUl = document.createElement("ul");
    tagsUl.className = "horizontal-list horizontal-list--gray";
    if (Array.isArray(article.taxonomies)) {
      for (const t of article.taxonomies) {
        if (!t) continue;
        const li = document.createElement("li");
        li.innerHTML = this.vcEncodeHtml(t);
        tagsUl.appendChild(li);
      }
    }
    boxContent.appendChild(tagsUl);

    // Abstract
    if (article.abstract && article.abstract.trim() !== "") {
      const p = document.createElement("p");
      p.innerHTML = this.vcEncodeHtml(article.abstract);
      boxContent.appendChild(p);
    }

    // Description
    if (article.description && article.description.trim() !== "") {
      const p = document.createElement("p");
      p.innerHTML = this.vcEncodeHtml(article.description);
      boxContent.appendChild(p);
    }

    // Content info (icons + actions)
    const contentInfo = document.createElement("div");
    contentInfo.className = "content-info";

    const iconsWrap = document.createElement("div");
    iconsWrap.className = "content-info__icons";
    if (article.isRead) {
      const readIcon = document.createElement("div");
      readIcon.className = "content-info__icon";
      readIcon.title = "Read";
      readIcon.setAttribute("data-bs-toggle", "tooltip");

      const img = document.createElement("img");
      img.src = "/images/read.svg";
      img.alt = "";
      img.className = "icon";
      readIcon.appendChild(img);

      const sr = document.createElement("span");
      sr.className = "screen-reader-text";
      sr.textContent = "Read";
      readIcon.appendChild(sr);

      iconsWrap.appendChild(readIcon);
    }

    const actionsWrap = document.createElement("div");
    actionsWrap.className = "content-info__actions";

    const actionLink = document.createElement("a");
    let targetHref = "#";
    let isExternal = false;

    if (article.externalUrl) {
      targetHref = this.resolveExternalUrl(article.externalUrl);
      try {
        if (targetHref !== "#") {
          const u = new URL(targetHref, window.location.origin);
          isExternal = u.origin !== window.location.origin;
        }
      } catch {
        isExternal = false;
      }
    } else {
      targetHref = this.resolveInternalUrl(article.url);
      isExternal = false;
    }

    // Encode URL before assigning to href
    const encodedTargetHref = encodeURI(targetHref);
    actionLink.setAttribute("href", encodedTargetHref);
    actionLink.className = "content-info__action";

    if (isExternal) {
      actionLink.target = "_blank";
      actionLink.rel = "noopener noreferrer";
      // Optionally: actionLink.referrerPolicy = "no-referrer";
    } else {
      actionLink.target = "_self";
    }

    const iconEl = this.buildActionIcon();
    actionLink.appendChild(iconEl);

    const srText = document.createElement("span");
    srText.className = "screen-reader-text";
    srText.textContent = "View article";
    actionLink.appendChild(srText);

    actionsWrap.appendChild(actionLink);

    contentInfo.appendChild(iconsWrap);
    contentInfo.appendChild(actionsWrap);
    boxContent.appendChild(contentInfo);

    return card;
  }

  private render(): void {
    if (!this.element || !this.searchService) {
      console.warn(
        "ArticleListingCard: Cannot render, element or searchService is null"
      );
      return;
    }

    if (this.searchSortButton) {
      this.searchSortButton.updateState();
    }

    // Rebuild content safely using DOM APIs
    // Clear existing but keep a reference to sentinel if present
    this.element.textContent = "";

    const frag = document.createDocumentFragment();
    const results: SearchItem[] =
      (this.searchService.context?.results as any[]) ?? [];

    for (const article of results) {
      try {
        const cardEl = this.buildCard(article as SearchItem);
        frag.appendChild(cardEl);
      } catch {
        continue;
      }
    }

    this.element.appendChild(frag);

    // Loading indicator
    if (this.searchService.isLoading) {
      const loading = document.createElement("div");
      loading.className = "infinite-loading";
      loading.textContent = "Loading...";
      loading.style.padding = "16px";
      loading.style.textAlign = "center";
      this.element.appendChild(loading);
    }

    // Re-append or create sentinel at the very end
    if (!this.sentinel) {
      this.sentinel = document.createElement("div");
      this.sentinel.className = "infinite-scroll-sentinel";
      this.sentinel.style.height = "1px";
      this.sentinel.style.width = "100%";
    }
    this.sentinel.style.display = this.searchService.hasMore()
      ? "block"
      : "none";
    this.element.appendChild(this.sentinel);

    // Ensure observer is active
    if (this.io && this.sentinel) {
      this.io.observe(this.sentinel);
    }

    const resultsCount = document.querySelector(
      "#results-count"
    ) as HTMLElement | null;
    if (resultsCount) {
      resultsCount.textContent = String(
        this.searchService.context?.totalCount ?? 0
      );
    }
  }

  public destroy(): void {
    if (this.searchSortButton) {
      this.searchSortButton.destroy();
      this.searchSortButton = null;
    }
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    if (this.searchService) {
      this.searchService.removeObserver(this.renderCallback);
    }
  }
}
